/**
 * Histopathology Reporting – Completed List.
 * GET /histopathology/result-page/?tab=completed with optional ?date=YYYY-MM-DD
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonValidation from "../../Utility/CommonValidation";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { ImagePaths } from "../../Utility/ImagePaths";
import { Colors } from "../../Styles/Colors";
import { CurrencySymbol } from "../../Utility/Constants";
import ResultEnterPopup from "./ResultEnterPopup";
import AmendedLogPopup from "./AmendedLogPopup";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import { stripServiceHead } from "../../Utility/ServiceNameFormat";
import "../../Styles/Configuration.css";

function PatientDetailsCell({ data }) {
  if (!data) return null;
  const name = CommonValidation.removeSpace(data?.name ?? data?.patient_name) || "-";
  const mobile = data?.patients_mobile ?? data?.mobile_no ?? "";
  const ageRaw = data?.age;
  const ageStr = ageRaw != null && ageRaw !== "" ? `${ageRaw} Y` : "";
  const g = String(data?.gender || "").trim().toUpperCase().charAt(0);
  const genderStr = g === "M" ? "Male" : g === "F" ? "Female" : g === "T" ? "Transgender" : "";
  const ageGender = ageStr && genderStr ? `${ageStr} / ${genderStr}` : (ageStr || genderStr);
  const sub = [ageGender, mobile].filter(Boolean).join(" | ");
  return (
    <Box sx={{ lineHeight: 1.3 }}>
      {name.length > 25 ? (
        <Tooltip title={name.toUpperCase()} placement="top" arrow>
          <Typography variant="body2" fontWeight={600} sx={{ display: "block", margin: 0, textTransform: "uppercase" }}>{name.slice(0, 22) + "..."}</Typography>
        </Tooltip>
      ) : (
        <Typography variant="body2" fontWeight={600} sx={{ display: "block", margin: 0, textTransform: "uppercase" }}>{name}</Typography>
      )}
      {sub ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", margin: 0, marginTop: "2px" }}>{sub}</Typography> : null}
    </Box>
  );
}

const menuItems = [
  { value: "Amendment", label: "Amendment" },
  { value: "Amended Log", label: "Amended Log" },
  { value: "Send Reports", label: "Send Reports" },
  { value: "Sent Reports Log", label: "Sent Reports Log" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CompletedList() {
  const [states, setState] = useState({
    completedList: [],
    page: 0,
    pageSize: 10,
    fromDate: DateTime.now().toFormat("yyyy-MM-dd"),
    dateIndex: 1,
    loading: false,
    isErrorMsg: false,
    isErrorMsgText: "",
    isSuccessMsg: false,
    isSuccessMsgText: "",
    signatureData: [],
    templateData: [],
    resultPopupData: null,
    resultPopupLoading: false,
    templatePopUpOpen: false,
    selectedPatient: null,
    anchorEl: null,
    selectedRow: null,
    selectedRowData: null,
    openForAmendment: false,
    amendedLogOpen: false,
    amendedLogRow: null,
    sendReportOpen: false,
    sendReportRow: null,
    sendReportSentBy: "",
    sendReportReceiver: "",
    sendReportSubmitting: false,
    sendReportLogOpen: false,
    sendReportLogRow: null,
    sendReportLogs: [],
    sendReportLogLoading: false,
    userList: [],
    selectedSentBy: null,
  });

  useEffect(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_ESIGN_CONFIG)
      .then((res) => {
        if (res?.data?.status === "success") {
          const list = res.data.data?.filter((item) => item.status) || [];
          setState((prev) => ({ ...prev, signatureData: list }));
        }
      })
      .catch((err) => setState((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: err?.response?.data?.message || err?.message })));
    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_USER_ACCESS_ROLE_MAPPING}?include_admin=1`)
      .then((res) => {
        if (res?.data?.status === "success") {
          setState((prev) => ({ ...prev, userList: res.data.data || [] }));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!states.templatePopUpOpen || !states.selectedPatient?.bill_id) {
      if (!states.templatePopUpOpen) setState((prev) => ({ ...prev, templateData: [] }));
      return;
    }
    const billId = states.selectedPatient.bill_id;
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_TEMPLATE}?bill_id=${billId}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setState((prev) => ({ ...prev, templateData: res.data.data ?? [] }));
        } else {
          setState((prev) => ({ ...prev, templateData: [] }));
        }
      })
      .catch(() => setState((prev) => ({ ...prev, templateData: [] })));
  }, [states.templatePopUpOpen, states.selectedPatient?.bill_id]);

  useEffect(() => {
    if (!states.templatePopUpOpen || !states.selectedPatient) {
      setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
      return;
    }
    const resultDetailId = states.selectedPatient.action_id ?? states.selectedPatient.result_detail_id;
    if (!resultDetailId) {
      setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
      return;
    }
    setState((prev) => ({ ...prev, resultPopupLoading: true }));
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_POPUP}?result_detail_id=${resultDetailId}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success" && res?.data?.data) {
          setState((prev) => ({ ...prev, resultPopupData: res.data.data, resultPopupLoading: false }));
        } else {
          setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          resultPopupData: null,
          resultPopupLoading: false,
          isErrorMsg: true,
          isErrorMsgText: err?.response?.data?.message ?? err?.message ?? "Failed to load popup data.",
        }));
      });
  }, [states.templatePopUpOpen, states.selectedPatient?.action_id, states.selectedPatient?.result_detail_id]);

  const fetchCompletedList = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
    const date = states.fromDate || DateTime.now().toFormat("yyyy-MM-dd");
    const params = new URLSearchParams();
    params.set("tab", "completed");
    if (date) params.set("date", date);
    const query = params.toString();
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE}?${query}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          const list = res.data.data || [];
          const mapped = list.map((item) => ({
            ...item,
            action_id: item.result_detail_id,
            name: item.patient_name ?? item.name,
            patients_mobile: item.mobile_no ?? item.patients_mobile,
            patient_account_number: item.patient_identifier ?? item.patient_account_number,
            net_amount: item.bill_amount ?? item.net_amount,
            balance_amount: item.balance ?? item.balance_amount,
            test_name: stripServiceHead(item.test_names ?? item.test_name),
          }));
          setState((prev) => ({ ...prev, completedList: mapped, loading: false }));
        } else {
          setState((prev) => ({ ...prev, completedList: [], loading: false }));
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          completedList: [],
          loading: false,
          isErrorMsg: true,
          isErrorMsgText: err?.response?.data?.message || err?.message || "Failed to load completed list",
        }));
      });
  }, [states.fromDate]);

  useEffect(() => {
    fetchCompletedList();
  }, [fetchCompletedList]);

  const errorMessage = (msg) => setState((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: msg }));
  const successMessage = (msg) => setState((prev) => ({ ...prev, isSuccessMsg: true, isSuccessMsgText: msg }));
  const msgClose = () => setState((prev) => ({ ...prev, isErrorMsg: false, isErrorMsgText: "", isSuccessMsg: false, isSuccessMsgText: "" }));

  const printResult = useCallback((row) => {
    if (!row) {
      errorMessage("No row selected for print.");
      return;
    }
    const resultId = row.action_id ?? row.result_detail_id;
    const patientId = row.patient_id;
    const billDate = row.bill_date ?? states.fromDate;
    const dateStr = billDate ? (typeof billDate === "string" ? billDate : DateTime.fromJSDate(new Date(billDate)).toFormat("yyyy-MM-dd")) : DateTime.now().toFormat("yyyy-MM-dd");

    let url = Serviceurls.HISTOPATHOLOGY_RESULT_PRINT;
    const params = new URLSearchParams();
    if (resultId != null && resultId !== "") {
      params.set("result_id", String(resultId));
    } else if (patientId != null && patientId !== "") {
      params.set("patient_id", String(patientId));
      params.set("date", dateStr);
    } else {
      errorMessage("Cannot print: missing result_id or patient_id.");
      return;
    }
    params.set("export_type", "pdf");
    url = `${url}?${params.toString()}`;

    RestAPIService.PrintPDF(url)
      .then((response) => {
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      })
      .catch((err) => {
        errorMessage(err?.response?.data?.message || err?.message || "Failed to load print.");
      });
  }, [states.fromDate, errorMessage]);

  const dataURLtoFile = (dataUrl, fileName = "capture.jpg") => {
    if (!dataUrl || typeof dataUrl !== "string") return null;
    try {
      const arr = dataUrl.split(",");
      const mime = arr[0].match(/:(.*?);/)?.[1] ?? "image/jpeg";
      const bstr = atob(arr[1] ?? "");
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
      return new File([u8arr], fileName, { type: mime });
    } catch {
      return null;
    }
  };

  const postAmendmentData = (resultHtml, selectedLabIncharge, selectedPathologist2, options = {}) => {
    const row = states.selectedPatient;
    const resultDetailId = row?.action_id ?? row?.result_detail_id;
    if (!resultDetailId) {
      errorMessage("Invalid row. Please try again.");
      return;
    }
    if (!resultHtml?.replace(/<[^>]*>/g, "").trim()) {
      errorMessage("Please enter Result content.");
      return;
    }
    const formData = new FormData();
    formData.append("result_detail_id", resultDetailId);
    formData.append("status", "save");
    formData.append("report_content", resultHtml?.trim() ?? "");
    formData.append("esign_left_id", selectedLabIncharge?.id ?? "");
    formData.append("esign_right_id", selectedPathologist2?.id ?? "");
    if (Array.isArray(options?.pdfUploadList) && options.pdfUploadList.length > 0) {
      options.pdfUploadList.forEach((item) => {
        const file = item?.document_file ?? item?.file ?? item;
        const name = item?.document_name ?? item?.name ?? "file";
        if (file instanceof Blob || file instanceof File) {
          formData.append("result_images", file, name);
        }
      });
    }
    if (Array.isArray(options?.result_document) && options.result_document.length > 0) {
      formData.append("result_document", JSON.stringify(options.result_document));
    }
    setState((prev) => ({ ...prev, loading: true }));
    RestAPIService.postFormData(formData, Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_AMEND)
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res?.data?.message ?? "Result amended successfully.");
          setState((prev) => ({
            ...prev,
            loading: false,
            templatePopUpOpen: false,
            selectedPatient: null,
            resultPopupData: null,
            openForAmendment: false,
          }));
          fetchCompletedList();
        } else {
          setState((prev) => ({ ...prev, loading: false }));
          errorMessage(res?.data?.message ?? "Amendment failed.");
        }
      })
      .catch((err) => {
        setState((prev) => ({ ...prev, loading: false }));
        errorMessage(err?.response?.data?.message ?? err?.message ?? "Failed to amend result.");
      });
  };

  const rows = useMemo(() => {
    const list = states.completedList || [];
    return list.map((el, i) => ({
      ...el,
      sno: i + 1,
      patientdetils: `${CommonValidation.removeSpace(el?.name) || ""} ${el?.patients_mobile || ""} ${el?.patient_account_number || ""}`,
    }));
  }, [states.completedList]);

  const renderMenu = (data) => (
    <Box component="div" display="flex">
      <Tooltip title="More" placement="top" arrow>
        <Button
          className="Common_Btn_Min_Width"
          onClick={(e) => setState((prev) => ({
            ...prev,
            anchorEl: e.currentTarget,
            selectedRow: data?.action_id ?? data?.result_detail_id ?? data?.sno,
            selectedRowData: data,
          }))}
        >
          <MoreVertIcon sx={{ color: "black" }} />
        </Button>
      </Tooltip>
      {states.selectedRow === (data?.action_id ?? data?.result_detail_id ?? data?.sno) && (
        <Menu
          anchorEl={states.anchorEl}
          open={Boolean(states.anchorEl)}
          onClose={() => setState((prev) => ({ ...prev, anchorEl: null, selectedRowData: null }))}
          elevation={1}
        >
          {menuItems?.map((item, idx) => (
            <MenuItem
              key={idx}
              onClick={() => {
                if (item.value === "Amendment") {
                  setState((prev) => ({
                    ...prev,
                    anchorEl: null,
                    selectedRowData: null,
                    selectedPatient: prev.selectedRowData ?? data,
                    templatePopUpOpen: true,
                    openForAmendment: true,
                  }));
                } else if (item.value === "Amended Log") {
                  setState((prev) => ({
                    ...prev,
                    anchorEl: null,
                    amendedLogOpen: true,
                    amendedLogRow: prev.selectedRowData ?? data,
                  }));
                } else if (item.value === "Send Reports") {
                  const row = states.selectedRowData ?? data;
                  const resultId = row?.action_id ?? row?.result_detail_id;
                  setState((prev) => ({
                    ...prev,
                    anchorEl: null,
                    sendReportOpen: true,
                    sendReportRow: row,
                    // Leave "Sent By" blank so the sender explicitly picks a name.
                    selectedSentBy: null,
                    sendReportSentBy: "",
                    sendReportReceiver: "",
                    sendReportSubmitting: false,
                  }));
                  if (resultId) {
                    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_RESULT_EMAIL_REPORT}?result_detail_id=${resultId}`)
                      .then((res) => {
                        if (res?.data?.status === "success") {
                          const last = res.data.data?.last_receiver_email || "";
                          setState((prev) => ({ ...prev, sendReportReceiver: last }));
                        }
                      })
                      .catch(() => {});
                  }
                } else if (item.value === "Sent Reports Log") {
                  const row = states.selectedRowData ?? data;
                  const resultId = row?.action_id ?? row?.result_detail_id;
                  setState((prev) => ({
                    ...prev,
                    anchorEl: null,
                    sendReportLogOpen: true,
                    sendReportLogRow: row,
                    sendReportLogs: [],
                    sendReportLogLoading: true,
                  }));
                  if (resultId) {
                    RestAPIService.getAll(`${Serviceurls.HISTOPATHOLOGY_RESULT_EMAIL_REPORT}?result_detail_id=${resultId}`)
                      .then((res) => {
                        if (res?.data?.status === "success") {
                          setState((prev) => ({ ...prev, sendReportLogs: res.data.data?.logs || [], sendReportLogLoading: false }));
                        } else {
                          setState((prev) => ({ ...prev, sendReportLogs: [], sendReportLogLoading: false }));
                        }
                      })
                      .catch(() => setState((prev) => ({ ...prev, sendReportLogs: [], sendReportLogLoading: false })));
                  } else {
                    setState((prev) => ({ ...prev, sendReportLogLoading: false }));
                  }
                } else {
                  setState((prev) => ({ ...prev, anchorEl: null }));
                }
              }}
            >
              {item?.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );

  const columns = useMemo(
    () => [
      {
        field: "nxt_id",
        headerName: "Unique ID",
        flex: 0.07,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.8dvw" }}>{params?.row?.nxt_id ?? "-"}</Box>
        ),
      },
      {
        field: "sample_id",
        headerName: "Biopsy Number",
        flex: 0.11,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.8dvw" }}>{params?.row?.sample_id ?? "-"}</Box>
        ),
      },
      {
        field: "patientdetils",
        headerName: "Patient Name",
        flex: 0.2,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box>
            <PatientDetailsCell data={params?.row} />
          </Box>
        ),
      },
      {
        field: "test_name",
        headerName: "Test Name",
        flex: 0.2,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.8dvw" }}>
            {params?.row?.test_name?.length > 30 ? (
              <Tooltip title={params.row.test_name} placement="top" arrow>
                <Box component="span">{params.row.test_name.slice(0, 30) + "..."}</Box>
              </Tooltip>
            ) : (
              <Box>{params?.row?.test_name || "-"}</Box>
            )}
          </Box>
        ),
      },
      {
        field: "specimen",
        headerName: "Specimen",
        flex: 0.12,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const val = params?.row?.specimen ?? "-";
          return (
            <Tooltip title={val} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: "0.8dvw",
                  width: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {val}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "net_amount",
        headerName: "Payment Status",
        flex: 0.14,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box mt={"0.5vw"}>
            <Typography variant="body2" sx={{ fontSize: "0.8dvw" }}>
              Bill : {CommonValidation.AmountFormat(params.row.net_amount)}
            </Typography>
            <Typography variant="body2" color="error" sx={{ fontSize: "0.8dvw" }}>
              Balance : {CommonValidation.AmountFormat(params.row.balance_amount)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "pre_approved_by",
        headerName: "Pre Approved By",
        flex: 0.14,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const name = params?.row?.pre_approved_by || "-";
          const iso = params?.row?.pre_approved_date;
          const stamp = iso ? DateTime.fromISO(iso).toFormat("dd-LL-yyyy hh:mm a") : "";
          return (
            <Tooltip title={stamp ? `${name} • ${stamp}` : name} placement="top" arrow>
              <Box sx={{ lineHeight: 1.2, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.8dvw", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {name}
                </Typography>
                {stamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7dvw", whiteSpace: "nowrap" }}>
                    {stamp}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "approved_by",
        headerName: "Approved By",
        flex: 0.12,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const name = params?.row?.approved_by || "-";
          const iso = params?.row?.approved_date;
          const stamp = iso ? DateTime.fromISO(iso).toFormat("dd-LL-yyyy hh:mm a") : "";
          return (
            <Tooltip title={stamp ? `${name} • ${stamp}` : name} placement="top" arrow>
              <Box sx={{ lineHeight: 1.2, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.8dvw", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {name}
                </Typography>
                {stamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.7dvw", whiteSpace: "nowrap" }}>
                    {stamp}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        headerAlign: "left",
        align: "left",
        flex: 0.14,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mt: "0.3vw" }}>
            <Tooltip title="View" placement="top" arrow>
              <IconButton
                size="small"
                sx={{ minWidth: 40 }}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    templatePopUpOpen: true,
                    selectedPatient: params?.row,
                  }))
                }
              >
                <Box component="img" sx={{ width: "1.5dvw", height: "1.5dvw" }} src={ImagePaths.LabViewButton?.default ?? ImagePaths.LabViewButton} alt="view" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Result Print" placement="top" arrow>
              <IconButton
                size="small"
                sx={{ minWidth: 40 }}
                onClick={() => {
                  printResult(params?.row);
                }}
              >
                <Box component="img" sx={{ width: "1.5dvw", height: "1.5dvw" }} src={ImagePaths.PrintOn?.default} alt="print" />
              </IconButton>
            </Tooltip>
            {renderMenu(params?.row)}
          </Box>
        ),
      },
    ],
    [states.anchorEl, states.selectedRow]
  );

  return (
    <Box sx={{ width: "100%", height: "80vh", backgroundColor: Colors?.Background, padding: "0.5vw", boxSizing: "border-box" }}>
      <Box
        className="SpecimenCollection-page"
        sx={{ width: "100%", height: "78vh", backgroundColor: "white", display: "flex", flexDirection: "column", overflow: "hidden", boxSizing: "border-box" }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: "relative",
            zIndex: 10,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            p: 1.5,
            backgroundColor: "#fafafa",
            borderBottom: "1px solid #e0e0e0",
            minHeight: "8.5vh",
            flexShrink: 0,
            overflow: "visible",
          }}
        >
          <TextField
            size="small"
            type="date"
            label="Date"
            value={states.fromDate}
            onChange={(e) => setState((prev) => ({ ...prev, fromDate: e.target.value || DateTime.now().toFormat("yyyy-MM-dd") }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: "10vw" }}
          />
        </Box>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => String(row.action_id ?? row.result_detail_id ?? row.bill_id ?? row.patient_id ?? row.sno ?? "")}
          paginationModel={{ page: states.page, pageSize: states.pageSize }}
          onPaginationModelChange={(m) => setState((prev) => ({ ...prev, page: m.page, pageSize: m.pageSize }))}
          pageSizeOptions={[10, 20, 30]}
          pagination
          rowHeight={52}
          columnHeaderHeight={40}
          disableRowSelectionOnClick
          loading={states.loading}
          density="standard"
          showToolbar
          localeText={{
            toolbarColumns: "",
            toolbarDensity: "",
            toolbarExport: "",
            toolbarFilters: "",
            toolbarExportPrint: "",
          }}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                No Records To Be Shown
              </Box>
            ),
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            position: "relative",
            zIndex: 1,
            "& .MuiDataGrid-toolbarContainer": { position: "relative", zIndex: 5 },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, color: "#616161" },
            "& .MuiDataGrid-cell": {},
          }}
        />
      </Box>

      <ResultEnterPopup
        open={!!states.templatePopUpOpen}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            templatePopUpOpen: false,
            selectedPatient: null,
            resultPopupData: null,
            openForAmendment: false,
          }))
        }
        signatureData={states.signatureData || []}
        templateData={states.resultPopupData?.template_options ?? states.templateData ?? []}
        popupData={states.resultPopupData}
        popupLoading={states.resultPopupLoading}
        errorMessage={errorMessage}
        onSave={states.openForAmendment ? postAmendmentData : () => { }}
        selectedServiceData={states.selectedPatient}
        resultTextData={states.selectedPatient?.result_data}
        editorDisable={!canEditTab("Completed List") || !states.openForAmendment}
        isDispatchshown={false}
        viewOnly={!states.openForAmendment}
        postButtonLabel={states.openForAmendment ? "Update" : undefined}
        showOnlyApproveButton={!!states.openForAmendment}
      />

      <AmendedLogPopup
        open={!!states.amendedLogOpen}
        onClose={() => setState((prev) => ({ ...prev, amendedLogOpen: false, amendedLogRow: null }))}
        resultDetailId={states.amendedLogRow?.action_id ?? states.amendedLogRow?.result_detail_id}
      />

      {/* Send Reports popup */}
      <Dialog
        open={!!states.sendReportOpen}
        onClose={() => !states.sendReportSubmitting && setState((prev) => ({ ...prev, sendReportOpen: false, sendReportRow: null }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1vw", fontWeight: 700 }}>Send Report</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
            {states.sendReportRow?.name && (
              <Typography variant="caption" color="text.secondary">
                Patient: {String(states.sendReportRow?.name || "").toUpperCase()}
              </Typography>
            )}
            <Autocomplete
              size="small"
              fullWidth
              options={states.userList || []}
              getOptionLabel={(opt) => {
                if (!opt) return "";
                if (typeof opt === "string") return opt;
                const name = [opt.first_name, opt.last_name].filter(Boolean).join(" ");
                return name || opt.username || "";
              }}
              isOptionEqualToValue={(a, b) => a?.id === b?.id || a?.username === b?.username}
              value={states.selectedSentBy}
              onChange={(_e, newVal) => setState((prev) => ({
                ...prev,
                selectedSentBy: newVal || null,
                sendReportSentBy: newVal?.username || "",
              }))}
              disableClearable
              disabled={states.sendReportSubmitting}
              renderInput={(params) => (
                <TextField {...params} label="Sent By" required error={!states.selectedSentBy} />
              )}
            />
            <TextField
              size="small"
              label="Receiver Mail ID"
              fullWidth
              value={states.sendReportReceiver}
              onChange={(e) => setState((prev) => ({ ...prev, sendReportReceiver: e.target.value }))}
              inputProps={{ maxLength: 100 }}
              disabled={states.sendReportSubmitting}
              placeholder="patient@example.com"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onClick={() => setState((prev) => ({ ...prev, sendReportOpen: false, sendReportRow: null }))}
            disabled={states.sendReportSubmitting}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={
              states.sendReportSubmitting ||
              !states.sendReportSentBy?.trim() ||
              !EMAIL_RE.test((states.sendReportReceiver || "").trim())
            }
            onClick={() => {
              const row = states.sendReportRow;
              const resultId = row?.action_id ?? row?.result_detail_id;
              if (!resultId) return;
              const payload = {
                result_detail_id: resultId,
                sent_by: states.sendReportSentBy.trim(),
                receiver_email: states.sendReportReceiver.trim(),
              };
              setState((prev) => ({ ...prev, sendReportSubmitting: true }));
              RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_RESULT_EMAIL_REPORT)
                .then((res) => {
                  if (res?.data?.status === "success") {
                    setState((prev) => ({
                      ...prev,
                      sendReportSubmitting: false,
                      sendReportOpen: false,
                      sendReportRow: null,
                      isSuccessMsg: true,
                      isSuccessMsgText: res.data.message || "Report emailed successfully",
                    }));
                  } else {
                    setState((prev) => ({
                      ...prev,
                      sendReportSubmitting: false,
                      isErrorMsg: true,
                      isErrorMsgText: res?.data?.message || "Failed to send email",
                    }));
                  }
                })
                .catch((err) => {
                  setState((prev) => ({
                    ...prev,
                    sendReportSubmitting: false,
                    isErrorMsg: true,
                    isErrorMsgText: err?.response?.data?.message || err?.message || "Failed to send email",
                  }));
                });
            }}
          >
            {states.sendReportSubmitting ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sent Reports Log popup */}
      <Dialog
        open={!!states.sendReportLogOpen}
        onClose={() => setState((prev) => ({ ...prev, sendReportLogOpen: false, sendReportLogRow: null, sendReportLogs: [] }))}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1vw", fontWeight: 700 }}>Sent Reports Log</DialogTitle>
        <DialogContent>
          {states.sendReportLogLoading ? (
            <Typography sx={{ py: 3, textAlign: "center" }}>Loading...</Typography>
          ) : states.sendReportLogs.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: "center", color: "#666" }}>No emails sent yet for this report.</Typography>
          ) : (
            <>
              {/* <Typography sx={{ fontSize: "0.75vw", color: "#666", mb: 1 }}>
                <strong>Sent</strong> means the SMTP server accepted the message for delivery. If the recipient's mailbox does not exist, a bounce notice is delivered to the sender's inbox — it is not shown here.
              </Typography> */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F5F8F7" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Sent At</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sent By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Receiver</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {states.sendReportLogs.map((l) => {
                      const isOk = l.status === "success";
                      const label = isOk ? "Sent" : "Failed";
                      return (
                        <TableRow key={l.id}>
                          <TableCell sx={{ fontSize: "0.85vw" }}>{l.sent_at || "-"}</TableCell>
                          <TableCell sx={{ fontSize: "0.85vw" }}>{l.sent_by || "-"}</TableCell>
                          <TableCell sx={{ fontSize: "0.85vw" }}>{l.receiver_email || "-"}</TableCell>
                          <TableCell sx={{ fontSize: "0.85vw", color: isOk ? "#2e7d32" : "#d32f2f", fontWeight: 600 }}>
                            {label}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.75vw", color: "#666" }}>
                            {l.error_message ? (
                              <Tooltip title={l.error_message} placement="top" arrow>
                                <Typography component="span" sx={{ fontSize: "0.75vw", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: 220 }}>
                                  {l.error_message}
                                </Typography>
                              </Tooltip>
                            ) : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setState((prev) => ({ ...prev, sendReportLogOpen: false, sendReportLogRow: null, sendReportLogs: [] }))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {states.isErrorMsg && <ToastMsg severity="error" msg={states.isErrorMsgText} msgPop={msgClose} />}
      {states.isSuccessMsg && <ToastMsg severity="success" msg={states.isSuccessMsgText} msgPop={msgClose} />}
    </Box>
  );
}
